import {useEffect, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {GET_BOTMAKER_SETTINGS} from '../../gql/queries/botmaker'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {CREATE_BOTMAKER_WHATSAPP_TEMPLATE} from '../../gql/mutations/botmaker'
import {WhatsAppPreview} from '../templates/components/tiptap/WhatsAppPreview'
import {WhatsAppEditor} from '../templates/components/tiptap/WhatsAppEditor'
import {BOTMAKER_VARIABLES, DEFAULT_TEMPLATE_VARIABLES} from '../templates/variables'

const CATEGORY_OPTIONS = [
  'UTILITY',
  'MARKETING',
  'TRANSACTIONAL',
  'AUTO_REPLY',
  'ALERT_UPDATE',
  'APPOINTMENT_UPDATE',
  'AUTHENTICATION',
]

type ButtonConfig = {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'
  text: string
  value: string // intentIdOrName, url, or phone number
}

interface ICreateBotmakerTemplateModalProps {
  show: boolean
  onHide: () => void
  onSuccess: () => void
}

export const CreateBotmakerTemplateModal = ({
  show,
  onHide,
  onSuccess,
}: ICreateBotmakerTemplateModalProps) => {
  const [form, setForm] = useState<{
    name: string
    locale: string
    category: string
    botName: string
    phoneNumber: string
    optInImage: string
    bodyText: string
    namespace: string
    headerType: 'NONE' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'
    headerUrl: string
    footerText: string
    buttons: ButtonConfig[]
  }>({
    name: '',
    locale: 'es',
    category: 'UTILITY',
    botName: '',
    phoneNumber: '',
    optInImage: 'https://storage.googleapis.com/storage.botmaker.com/public/res/thefutureinsurance/20251211-ZR4fReIhcgcwoqjNyIzAGVtE3Gu1-TB30W-b3B0LWV2aWRlbmNpYS1ldmlkZW5jZQ==.jpg',
    bodyText: '',
    namespace: '',
    headerType: 'NONE',
    headerUrl: '',
    footerText: '',
    buttons: [],
  })

  const {data: settingsData} = useQuery(GET_BOTMAKER_SETTINGS)

  useEffect(() => {
    if (settingsData?.botmakerSettings) {
      setForm((prev) => ({
        ...prev, 
        namespace: settingsData.botmakerSettings.namespace || prev.namespace,
        botName: settingsData.botmakerSettings.botName || prev.botName,
        phoneNumber: settingsData.botmakerSettings.phoneLineNumber || prev.phoneNumber,
      }))
    }
  }, [settingsData])

  const [createTemplate, {loading: creating}] = useMutation(CREATE_BOTMAKER_WHATSAPP_TEMPLATE, {
    onCompleted: (res) => {
      console.info('Botmaker template create response', res)
      toast.success('Template submitted to Botmaker')
      setForm({
        name: '',
        locale: 'es',
        category: 'UTILITY',
        botName: settingsData?.botmakerSettings?.botName || '',
        phoneNumber: settingsData?.botmakerSettings?.phoneLineNumber || '',
        optInImage: 'https://storage.googleapis.com/storage.botmaker.com/public/res/thefutureinsurance/20251211-ZR4fReIhcgcwoqjNyIzAGVtE3Gu1-TB30W-b3B0LWV2aWRlbmNpYS1ldmlkZW5jZQ==.jpg',
        bodyText: '',
        namespace: settingsData?.botmakerSettings?.namespace || '',
        headerType: 'NONE',
        headerUrl: '',
        footerText: '',
        buttons: [],
      })
      onSuccess()
      onHide()
    },
    onError: (err) => {
      console.error('Botmaker template create error', err)
      toast.error(err.message || 'Error creating template')
    },
  })

  const handleCreate = () => {
    if (!form.bodyText) {
      toast.error('Body text is required')
      return
    }

    const payload: any = {
      name: form.name || null,
      locale: form.locale,
      category: form.category,
      botName: form.botName,
      phoneLineNumber: form.phoneNumber,
      optInImage: form.optInImage,
      bodyText: form.bodyText,
    }

    if (form.footerText) {
      payload.footer = JSON.stringify({text: form.footerText})
    }

    if (form.headerType !== 'NONE') {
      const headerObj = {
        format: form.headerType,
        exampleURL: form.headerUrl || 'https://via.placeholder.com/300',
      }
      payload.header = JSON.stringify(headerObj)
    }

    if (form.buttons.length > 0) {
      const buttonsArr = form.buttons.map((b) => {
        if (b.type === 'QUICK_REPLY') {
          return {
            type: 'QUICK_REPLY',
            text: b.text,
            intentIdOrName: b.value,
          }
        }
        return {
          type: b.type,
          text: b.text,
          url: b.type === 'URL' ? b.value : undefined,
          phoneNumber: b.type === 'PHONE_NUMBER' ? b.value : undefined,
        }
      })
      payload.buttons = JSON.stringify(buttonsArr)
    }

    createTemplate({
      variables: {
        input: payload,
      },
    })
  }

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>
            <div className='title-botmaker-template'>
                <h3>Create Botmaker Template</h3> 
                <span className='text-muted'>Bot {form.botName} ({form.phoneNumber})</span>
            </div>
            </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='row'>
          {/* Left Column: Form Configuration */}
          <div className='col-lg-8'>
            <div className='d-flex flex-wrap gap-3 mb-4'>
              <Form.Group style={{minWidth: 200}}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  placeholder='payment_reminder'
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                />
              </Form.Group>
              <Form.Group style={{minWidth: 100}}>
                <Form.Label>Locale</Form.Label>
                <Form.Select
                  value={form.locale}
                  onChange={(e) => setForm({...form, locale: e.target.value})}
                >
                  <option value='es'>es</option>
                  <option value='en'>en</option>
                </Form.Select>
              </Form.Group>
              <Form.Group style={{minWidth: 150}}>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='flex-grow-1'>
                <Form.Label>Namespace (WhatsApp)</Form.Label>
                <Form.Control
                  placeholder='e.g. f1a2b3c4_d5e6_7f8g...'
                  value={form.namespace}
                  onChange={(e) => setForm({...form, namespace: e.target.value})}
                  readOnly={!!settingsData?.botmakerSettings?.namespace}
                  disabled={!!settingsData?.botmakerSettings?.namespace}
                />
              </Form.Group>
            </div>

            {/* Header Config */}
            <div className='mb-4 border-bottom pb-3'>
              <Form.Label className='fw-bold'>Header (Multimedia)</Form.Label>
              <div className='d-flex gap-3 align-items-end'>
                <Form.Group style={{minWidth: 150}}>
                  <Form.Select
                    value={form.headerType}
                    onChange={(e) => setForm({...form, headerType: e.target.value as any})}
                  >
                    <option value='NONE'>None</option>
                    <option value='IMAGE'>Image</option>
                    <option value='VIDEO'>Video</option>
                    <option value='DOCUMENT'>Document</option>
                  </Form.Select>
                </Form.Group>
                {form.headerType !== 'NONE' && (
                  <Form.Group className='flex-grow-1'>
                    <Form.Control
                      placeholder='Media URL (e.g. https://...)'
                      value={form.headerUrl}
                      onChange={(e) => setForm({...form, headerUrl: e.target.value})}
                    />
                  </Form.Group>
                )}
              </div>
            </div>

            {/* Body */}
            <Form.Group className='mb-4'>
              <Form.Label className='fw-bold'>Body Message</Form.Label>
              <WhatsAppEditor
                value={form.bodyText}
                onChange={(text) => setForm({...form, bodyText: text})}
                height={200}
                placeholder='Hola ..'
                variables={[...BOTMAKER_VARIABLES, ...DEFAULT_TEMPLATE_VARIABLES]}
                getVariableText={(v) => `\${${v}}`}
              />
            </Form.Group>

            {/* Footer */}
            <Form.Group className='mb-4'>
              <Form.Label className='fw-bold'>Footer (Optional)</Form.Label>
              <Form.Control
                placeholder='Add a short footer text...'
                value={form.footerText}
                onChange={(e) => setForm({...form, footerText: e.target.value})}
              />
            </Form.Group>

            {/* Buttons */}
            <div className='mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <Form.Label className='fw-bold mb-0'>Buttons</Form.Label>
                <Button
                  variant='light'
                  size='sm'
                  onClick={() =>
                    setForm({
                      ...form,
                      buttons: [...form.buttons, {type: 'QUICK_REPLY', text: '', value: ''}],
                    })
                  }
                  disabled={form.buttons.length >= 3}
                >
                  <i className='bi bi-plus-circle me-1' /> Add Button
                </Button>
              </div>

              {form.buttons.map((btn, idx) => (
                <div
                  key={idx}
                  className='d-flex gap-2 mb-2 align-items-start bg-light p-2 rounded'
                >
                  <Form.Select
                    style={{width: '140px'}}
                    size='sm'
                    value={btn.type}
                    onChange={(e) => {
                      const newButtons = [...form.buttons]
                      newButtons[idx].type = e.target.value as any
                      setForm({...form, buttons: newButtons})
                    }}
                  >
                    <option value='QUICK_REPLY'>Quick Reply</option>
                    {/* <option value="URL">URL</option>
                              <option value="PHONE_NUMBER">Phone</option> */}
                  </Form.Select>

                  <Form.Control
                    placeholder='Button Text'
                    size='sm'
                    value={btn.text}
                    onChange={(e) => {
                      const newButtons = [...form.buttons]
                      newButtons[idx].text = e.target.value
                      setForm({...form, buttons: newButtons})
                    }}
                  />

                  <Form.Control
                    placeholder={btn.type === 'QUICK_REPLY' ? 'Intent ID / Name' : 'Value'}
                    size='sm'
                    value={btn.value}
                    onChange={(e) => {
                      const newButtons = [...form.buttons]
                      newButtons[idx].value = e.target.value
                      setForm({...form, buttons: newButtons})
                    }}
                  />

                  <Button
                    variant='outline-danger'
                    size='sm'
                    onClick={() => {
                      const newButtons = form.buttons.filter((_, i) => i !== idx)
                      setForm({...form, buttons: newButtons})
                    }}
                  >
                    <i className='bi bi-trash'></i>
                  </Button>
                </div>
              ))}
              {form.buttons.length === 0 && (
                <div className='text-muted small fst-italic'>No buttons added.</div>
              )}
            </div>
          </div>

          {/* Right Column: Live Preview */}
          <div className='col-lg-4'>
            <Form.Label className='fw-bold'>Live Preview</Form.Label>
            <WhatsAppPreview
              message={form.bodyText || 'Start typing to see preview...'}
              header={
                form.headerType !== 'NONE'
                  ? {
                      type: form.headerType as any,
                      url: form.headerUrl || undefined,
                      text: 'Header Text',
                    }
                  : undefined
              }
              footer={form.footerText}
              buttons={form.buttons.map((b) => ({
                type: b.type,
                text: b.text || 'Button',
              }))}
            />
          </div>
        </div>
        <div className='mb-4'>
            <Form.Group>
            <Form.Label>Opt-In Image URL (Required by BotMaker)</Form.Label>
            <Form.Control
                type='url'
                placeholder='https://...'
                value={form.optInImage}
                onChange={(e) => setForm({...form, optInImage: e.target.value})}
            />
            <Form.Text className='text-muted'>
                URL de la imagen que prueba el consentimiento del usuario (Requerido para Marketing).
            </Form.Text>
            </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={creating} variant='primary'>
          {creating ? <Spinner animation='border' size='sm' className='me-2' /> : null}
          Create Template
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
